import React from 'react'
import { Route, Redirect, Switch, withRouter } from 'react-router-dom'
import AcceptMembershipPanel from './AcceptMembershipPanel'
import SignUpAndAcceptMembershipPanel, {
  SignUpAndAcceptMembershipPanelProps,
} from './SignUpAndAcceptMembershipPanel'
import SignInAndAcceptMembershipPanel from './SignInAndAcceptMembershipPanel'
import SwitchAccountOrAcceptMembershipPanel from './SwitchAccountOrAcceptMembershipPanel'
import ForgotPasswordPanel from './ForgotPasswordPanel'
import { routes } from 'constants/routes'
import { Location, History } from 'history'
import { IsSignedIn, IsNotSignedIn } from 'containers/IsSignedIn'
import { useGetShareMembershipQuery } from 'gqlTypes'
import withToastConsumer from 'containers/ToastManager/withToastConsumer'
import NotFoundView from 'views/NotFoundView'
import InvalidInvitationView from 'views/InvalidInvitationView'
import LoadingState from 'components/LoadingState'
import * as Sentry from '@sentry/browser'
import jwtDecode from 'jwt-decode'
import { Flex } from 'components/Box'
import queryString from 'query-string'
import useUserProfile from 'modules/user/useUserProfile'
import { ToastContextValues } from 'containers/ToastManager/ToastContext'
import useAcceptShareMembershipInvitation from '../operations/useAcceptShareMembershipInvitation'
import { noop } from 'utils'
import useSignOut from 'modules/user/useSignOut'
import { StyledIndexLayout } from './shared'

const useInviteToken = (queryToken: string | string[] | null) => {
  let inviteToken: string | null = null
  let shareShortId: string | null = null
  let documentName: string | null = null

  if (queryToken) {
    inviteToken = Array.isArray(queryToken) ? queryToken[0] : queryToken
    try {
      // eslint-disable-next-line no-extra-semi
      ;({
        document_name: documentName,
        share_short_id: shareShortId,
      } = jwtDecode(inviteToken))
    } catch {
      inviteToken = shareShortId = documentName = null
    }
  }

  return { inviteToken, documentName, shareShortId }
}

interface AcceptShareInviteByInviteTokenViewProps {
  showToast?: ToastContextValues['showToast']
  history: History
  location: Location
}

interface AcceptInviteProps extends SignUpAndAcceptMembershipPanelProps {
  loading: boolean
  signOut: () => void
  invitedEmail: string
}
/**
 * We needed to move the routes logic into a separate component because
 * the component that was encapsulating all the logic wasn't updating
 * the user profile. Accesing it from this component works perfectly
 */
const AcceptInviteRoute = ({
  signOut,
  loading,
  invitedEmail,
  ...acceptShareMembershipPanelProps
}: AcceptInviteProps) => {
  const { data: profile } = useUserProfile()

  const userEmail = profile?.me.email
  const userAvatar = profile?.me.avatar?.small
  const isSSOAccount = !profile?.me.hasPersonalIdentity
  const isCurrentUserInvited = invitedEmail === userEmail

  // Just to get rid of TS errors
  if (!userEmail) return null

  if (isCurrentUserInvited && !isSSOAccount) {
    return (
      <AcceptMembershipPanel
        {...acceptShareMembershipPanelProps}
        userInfo={{
          userEmail,
          userAvatar,
        }}
        onRequestAccessWithDifferentAccount={signOut}
      />
    )
  }

  return (
    <SwitchAccountOrAcceptMembershipPanel
      {...acceptShareMembershipPanelProps}
      invitedEmail={invitedEmail}
      userInfo={{
        userEmail,
        userAvatar,
      }}
      loading={loading}
    />
  )
}

const AcceptShareInviteByInviteTokenView = (
  props: AcceptShareInviteByInviteTokenViewProps
) => {
  const { showToast = noop, history, location } = props

  const query = queryString.parse(location.search)
  const queryToken = query.token

  const { inviteToken, documentName, shareShortId } = useInviteToken(queryToken)

  const signOut = useSignOut({
    pathname: routes.SHARE_ACCEPT_INVITE_SIGN_UP.create({
      query,
    }),
    search: location.search,
    state: {
      from: location,
    },
  })

  if (!inviteToken) {
    Sentry.captureMessage('Failed to parse invite token')
  }

  const { data, error, loading } = useGetShareMembershipQuery({
    variables: { token: inviteToken },
    skip: !inviteToken,
  })

  const [
    acceptShareMembership,
    {
      loading: acceptShareMembershipLoading,
      error: acceptShareMembershipError,
    },
  ] = useAcceptShareMembershipInvitation({
    variables: { token: inviteToken },
    onCompleted: () => {
      showToast('Invite successfully accepted')
      history.push(routes.SHARE_VIEW.create({ shareID: shareShortId ?? '' }))
    },
    onError: 'do-nothing', // we use acceptShareMembershipError to show the error message
  })

  if (loading) {
    return <LoadingState />
  }

  if (!inviteToken || !shareShortId || !data || error) {
    return <NotFoundView />
  }

  const { shareMembership } = data

  if (!shareMembership) {
    return <InvalidInvitationView />
  }

  const invitedEmail = shareMembership?.email ?? ''

  if (shareMembership?.inviteStatus === 'ACCEPTED' && shareShortId !== null)
    return (
      <Redirect
        to={routes.SHARE_VIEW.create({
          shareID: shareShortId,
        })}
      />
    )

  const acceptShareMembershipPanelProps = {
    shareShortId,
    acceptShareMembership,
    acceptShareMembershipError,
    acceptShareMembershipLoading,
    inviter: shareMembership?.inviter,
    inviteToken,
    documentName,
    invitedEmail,
  } as SignUpAndAcceptMembershipPanelProps

  return (
    <StyledIndexLayout variant="teal" headerLink="none">
      <Flex
        maxWidth="512px"
        width="100%"
        alignSelf="center"
        flex="1"
        alignItems="center">
        <Switch>
          <Route path={routes.SHARE_ACCEPT_INVITE_FORGOT_PASSWORD.template()}>
            <ForgotPasswordPanel invitedEmail={invitedEmail} />
          </Route>
          <Route path={routes.SHARE_ACCEPT_INVITE_SIGN_UP.template()}>
            <SignUpAndAcceptMembershipPanel
              {...acceptShareMembershipPanelProps}
            />
          </Route>
          <Route path={routes.SHARE_ACCEPT_INVITE.template()}>
            <IsSignedIn>
              <AcceptInviteRoute
                signOut={signOut}
                loading={loading}
                {...acceptShareMembershipPanelProps}
              />
            </IsSignedIn>
            <IsNotSignedIn>
              <Redirect
                to={{
                  pathname: routes.SHARE_ACCEPT_INVITE_SIGN_IN.template(),
                  search: location.search,
                }}
              />
            </IsNotSignedIn>
          </Route>
          <Route path={routes.SHARE_ACCEPT_INVITE_SIGN_IN.template()}>
            <SignInAndAcceptMembershipPanel
              {...acceptShareMembershipPanelProps}
            />
          </Route>
          <Redirect
            to={{
              pathname: routes.SHARE_ACCEPT_INVITE.template(),
              search: location.search,
            }}
          />
        </Switch>
      </Flex>
    </StyledIndexLayout>
  )
}

export default withToastConsumer(withRouter(AcceptShareInviteByInviteTokenView))
